import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Grid,
  TableContainer,
  Tooltip,
  Link as MuiLink,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import Table from '@material-ui/core/Table'
import { DUMMY_DATA } from './Data'
import AddOligo from './AddOligo'
import { FormattedMessage, useIntl } from 'react-intl'
import configMessages from '../../messages'
import UtilMessages from '@utils/messages'
import DeleteAllDialog from '@src/routes/Configurators/components/DeleteDialog'
import { standardTubeRoute, useRouter } from '@src/routes'
import {
  ActiveLinkType,
  TubeProduct,
  TubeProperties,
  CountryType,
} from '../../utils/enum'
import { configContext } from '../../utils/StandardReducer'
import { ConfiguratorProductDetails } from '../../products/ConfiguratorSharedTypes'
import { useSessionFieldsMutation } from '@src/mutations/ConfiguratorTubesMutation.generated'
import { priceFormatter } from '@src/routes/Configurators/utils/PriceFormatter'
import styles from '@src/routes/Configurators/utils/styles'
import OligoNameWarning from '@src/routes/Configurators/validations/oligoNameWarning'
import { checkValidKey } from '@src/routes/Configurators/utils/util'

const useStyles = makeStyles((theme: Theme) => {
  return {
    cardWrapper: {
      backgroundColor: 'white',
      padding: theme.spacing(4, 5),
      marginBottom: theme.spacing(4),
      borderRadius: 0,
      boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
    },
    subHead: {
      fontSize: theme.typography.pxToRem(20),
      fontWeight: theme.typography.fontWeightBold,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: theme.spacing(3, 0),
    },
    dFlex: {
      display: 'flex',
    },
    cardContent: {
      padding: theme.spacing(0),
      marginBottom: theme.spacing(-5),
    },
    deleteMargin: {
      marginLeft: theme.spacing(5),
      marginRight: theme.spacing(5),
    },
    label: {
      fontWeight: 'bold',
    },
    deleteButton: {
      padding: theme.spacing(0),
    },
    deleteAllText: {
      fontSize: theme.typography.pxToRem(16),
    },
    sequenceInfo: {
      fontSize: theme.typography.pxToRem(14),
    },
    alignRight: {
      textAlign: 'right',
    },
    sequenceProperties: {
      width: '40%',
      '& + td': {
        paddingLeft: theme.spacing(1),
      },
    },
    additionalCarrierCSS: {
      width: '60%',
      '& + td': {
        paddingLeft: theme.spacing(1),
      },
    },
    dFlexForZHLang: {
      display: 'flex',
      paddingRight: theme.spacing(4.25),
    },
    tableContainer: {
      maxHeight: 600,
    },
    arrow: {
      color: theme.palette.common.white,
    },
    InfoIcon: {
      position: 'relative',
      fontSize: theme.typography.pxToRem(18),
      top: theme.spacing(1.5),
      marginLeft: theme.spacing(1.5),
    },
    tooltipRow: {
      display: 'table-column',
    },
    tooltipCell: {
      display: 'table-cell',
      padding: theme.spacing(2),
      maxWidth: theme.spacing(150),
    },
    tooltipCellSeq: {
      maxWidth: theme.spacing(50),
      fontSize: theme.typography.pxToRem(14),
    },
    deleteIcon: {
      fontSize: theme.typography.pxToRem(22),
      color: theme.palette.grey[400],
    },
  }
})

interface OrderTableProps {
  productDetails: ConfiguratorProductDetails
  updateError: any
  specificationsData: any
  errorMessage: string
}

export interface Data {
  id: number
  name: string
  base: string
  price: string
  scale: string
  purification: string
  amount: string
}

let data
data = DUMMY_DATA
export function performDelete(obj) {
  const array = data
  const index = array.indexOf(obj)
  if (index !== -1) {
    array.splice(index, 1)

    data = [...array]
  }
}
export function performDeleteAll() {
  data = []
}

const OrderTable: React.FC<OrderTableProps> = (props) => {
  // This param is use to set CSS
  const classes = useStyles()
  const GlobalCSS = styles()
  const intl = useIntl()
  const messages = { ...configMessages, ...UtilMessages }
  const [delDialogOpen, setDelDialogOpen] = useState(false)
  const [delItem, setDelItem] = useState()
  const [scrollBar, setScrollBar] = useState(false)
  const [delType, setDelType] = useState('')
  const { state, dispatch } = React.useContext(configContext)
  const { sessionData, OligoIndex, countryCode, productInformation } = state
  const { productDetails, updateError, specificationsData, errorMessage } =
    props
  const router = useRouter()
  const sequencePath = `${standardTubeRoute.index()}?product=${
    productDetails.productType
  }&activeLink=${ActiveLinkType.SequenceEntry}`
  const [sessionFields] = useSessionFieldsMutation()
  const [isNameWarning, setIsNameWarning] = React.useState(false)

  // to check if fiveMod or threeMod contains mA,mC,mG,mU
  const list: any = ['mA', 'mC', 'mG', 'mU']
  const isTwoMeBase = (modification) => {
    return list.includes(modification)
  }

  const sessionDataToSession = async (obj) => {
    await sessionFields({
      variables: { input: obj, productInput: productDetails },
    })
  }

  const tubes =
    specificationsData.specifications !== undefined &&
    specificationsData.specifications.filter(
      (spec) => spec.categoryName === TubeProperties.tubes
    )
  const quantitation =
    specificationsData.specifications !== undefined &&
    specificationsData.specifications.filter(
      (spec) => spec.categoryName === TubeProperties.quantitation
    )
  const conjugation =
    specificationsData.specifications !== undefined &&
    specificationsData.specifications.filter(
      (spec) => spec.categoryName === TubeProperties.conjugation
    )
  const conjugationAmount =
    specificationsData.specifications !== undefined &&
    specificationsData.specifications.filter(
      (spec) => spec.categoryName === TubeProperties.conjugationAmount
    )
  const conjugationChemistry =
    specificationsData.specifications !== undefined &&
    specificationsData.specifications.filter(
      (spec) => spec.categoryName === TubeProperties.conjugationChemistry
    )
  const additionalCarrierProtein =
    specificationsData.specifications !== undefined &&
    specificationsData.specifications.filter(
      (spec) => spec.categoryName === TubeProperties.additionalCarrierProtein
    )
  const isConjugationDisplayed = () => {
    if (conjugation.length === 0) {
      return false
    } else {
      if (conjugation[0].specificationModifications.length === 0) {
        return false
      }
    }
    return true
  }
  const isConjugationChemistryDisplayed = () => {
    if (conjugationChemistry.length === 0) {
      return false
    } else {
      if (conjugationChemistry[0].specificationModifications.length === 0) {
        return false
      }
    }
    return true
  }
  const isConjugationAmountDisplayed = () => {
    if (conjugationAmount.length === 0) {
      return false
    } else {
      if (conjugationAmount[0].specificationModifications.length === 0) {
        return false
      }
    }
    return true
  }
  const isAdditionalCarrierProteinDisplayed = () => {
    if (additionalCarrierProtein.length === 0) {
      return false
    } else {
      if (additionalCarrierProtein[0].specificationModifications.length === 0) {
        return false
      }
    }
    return true
  }
  const isTubeDisplayed = () => {
    if (tubes.length === 0) {
      return false
    } else {
      if (tubes[0].specificationModifications.length === 0) {
        return false
      }
    }
    return true
  }
  const isQuantitationDisplayed = () => {
    if (quantitation.length === 0) {
      return false
    } else {
      if (quantitation[0].specificationModifications.length === 0) {
        return false
      }
    }
    return true
  }
  const oligoNameWarning = (session) => {
    if (session && session.length !== 0) {
      let warning = false
      session.forEach((data) => {
        if (data.name && data.name.length > 18 && warning !== true) {
          warning = true
        }
      })
      setIsNameWarning(warning)
    }
  }
  React.useEffect(() => {
    if (
      sessionData[0] &&
      sessionData[0].additionalComments &&
      sessionData[0].additionalComments === ''
    ) {
      window.scrollTo(0, 0)
    }

    if (state.sessionData.length > 5) {
      setScrollBar(true)
    }
    oligoNameWarning(sessionData)
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData])

  const setInitialState = () => {
    return {
      additionalCarrierProtein: 'None',
      additionalComments: '',
      additionalServices: [],
      amount: '',
      applyToAll: false,
      autoValidation: '',
      baseCount: 0,
      carrierProtein: '',
      cartId: '',
      comments: '',
      concentration: '',
      conjugation: 'None',
      conjugationAmount: '',
      conjugationChemistry: '',
      countryCode: countryCode,
      crossContamination: '',
      crRnaType: '',
      cterminalMod: '',
      currencySymbol: '',
      customPeptideType: '',
      epsilon: 0,
      fiveMod: '',
      fiveModCategory: '',
      flag: 0,
      format: 'DRY',
      fullSOligoBackbone: false,
      gcContent: '',
      id: '1',
      internalModCategory: '',
      isLabelledAminoAcid: true,
      isoCode: false,
      isOverridden: false,
      itemId: '',
      listError: [
        {
          key: '',
          value: '',
        },
      ],
      material: '',
      meltingTemparature: '',
      minimumYield: '',
      molecularWeight: '',
      name: '',
      nterminalMod: '',
      oligoId: state.oligoCount + 1,
      orderItemId: '',
      otherValue: '',
      packageType: '',
      plateType: '',
      price: '',
      primerDimer: '',
      productType: productDetails.productType,
      purification: '',
      purityLevel: '',
      quantitation: '',
      scale: '',
      secondaryStructure: '',
      sequence: '',
      sequenceLocation: '',
      threeMod: '',
      threeModCategory: '',
      tubes: '1',
    }
  }

  // This method is use to perform delete and delete all operations
  const deleteOligoItem = (item, type) => {
    let isDeleteAll = false
    const maxItems =
      productDetails.productType === TubeProduct.iscaledna
        ? TubeProperties.MaximumOligosAllowedIscaledna
        : TubeProperties.MaximumOligosAllowed
    if (type === 'Selected' || type === 'SelectedViewAll') {
      if (OligoIndex !== 0) {
        const newIndex = OligoIndex - 1
        const array = [...state.sessionData]
        const index = array.indexOf(item)
        if (index !== -1) {
          array.splice(index, 1)
        }
        dispatch({
          type: 'UPDATE_OLIGO_INDEX',
          data: newIndex,
        })
        dispatch({
          type: 'UPDATE_CURRENT_OLIGO_INDEX',
          data: newIndex,
        })
        dispatch({
          type: 'UPDATE_INPUT',
          data: array,
        })
        if (array.length <= 5) {
          setScrollBar(false)
        }
        sessionDataToSession(array)
        oligoNameWarning(array)
      } else {
        isDeleteAll = true
      }
    } else if (type === 'All') {
      isDeleteAll = true
    }

    if (isDeleteAll) {
      sessionDataToSession([])

      router.push(sequencePath)

      dispatch({
        type: 'UPDATE_OLIGO_INDEX',
        data: 0,
      })
      dispatch({ type: 'IS_TUBE_PAGE_LOAD', data: false })
      dispatch({
        type: 'UPDATE_CURRENT_OLIGO_INDEX',
        data: 0,
      })
      dispatch({
        type: 'UPDATE_INPUT',
        data: [setInitialState()],
      })
      dispatch({
        type: 'IS_LABEL_AMINO_ACID_ERROR',
        data: false,
      })
      dispatch({ type: 'IS_SIDE_NAV_DISABLE', data: false })
      dispatch({ type: 'SET_PAGE_ERROR', data: '' })
      dispatch({
        type: 'UPDATE_ERROR_DATA',
        data: {
          ...state.errorData,
          name: '',
          sequence: '',
          scale: '',
          purification: '',
          fiveMod: '',
          threeMod: '',
          researcher: '',
          format: 'DRY',
          concentration: '',
          tubes: '1',
          countryCode: countryCode,
          fullSOligoBackbone: false,
          isOverridden: false,
          comments: '',
          cterminalMod: '',
          nterminalMod: '',
          otherValue: '',
          isLabelledAminoAcid: true,
          quantitation: '',
          purityLevel: '',
          amount: '',
          conjugation: '',
          conjugationAmount: '',
          conjugationChemistry: '',
          additionalCarrierProtein: '',
          additionalServices: [],
          flag: 0,
        },
      })
      if (state.sessionData.length <= 5) {
        setScrollBar(false)
      }
      dispatch({ type: 'IS_CONFIRM_ORDER', data: false })
    }

    dispatch({
      type: 'UPDATE_PAGE_LOAD',
      data: 1,
    })
    if (
      errorMessage.includes(
        intl.formatMessage(messages.CONF_MAX_ITEMS_ALLOWED, { maxItems })
      )
    ) {
      updateError(false)
    }
    dispatch({ type: 'FOCUS_ON_SEQUENCEBOX', data: false })
    dispatch({ type: 'UPDATE_OPTIONAL_BASES', data: '' })
    dispatch({ type: 'UPDATE_AQUA_INTERNAL_MOD', data: '' })
  }

  const handleDelete = (item) => {
    setDelDialogOpen(true)
    setDelType('Selected')
    setDelItem(item)
  }
  // This method is use to render data

  const displaySessionData = (index) => {
    if (errorMessage !== undefined && errorMessage !== '') {
      updateError(intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2))
    } else {
      dispatch({ type: 'IS_COMING_FROM_CONFIRM_PAGE', data: false })
      router.push(sequencePath)
      dispatch({
        type: 'UPDATE_CURRENT_OLIGO_INDEX',
        data: index,
      })
      dispatch({
        type: 'IS_VALID_TOTAL_VALIDATION',
        data: false,
      })
      dispatch({
        type: 'UPDATE_PAGE_LOAD',
        data: 1,
      })
    }
  }

  const totalOligos = () => {
    if (
      productDetails.productType === TubeProduct.Aqua ||
      productDetails.productType === TubeProduct.Cpeptide
    ) {
      return <FormattedMessage {...messages.CONF_TOTAL_PEPTIDE_COUNT} />
    } else {
      return <FormattedMessage {...messages.CONF_TOTAL_OLIGO_COUNT} />
    }
  }

  const renderData = () => {
    const result: any[] = []
    let concentration: string
    let scale: string
    let price: string
    let amount: string
    let viewSequence: string
    let conjugationAmountData: string
    sessionData.forEach((item, index) => {
      concentration = ''
      scale = ''
      amount = ''
      price = ''
      viewSequence = ''
      conjugationAmountData = ''
      if (item.concentration) {
        concentration = item.concentration?.replace('um', 'UM')
      }
      if (item.scale) scale = item.scale?.replace(/[^a-zA-Z0-9]/g, '_')
      if (item.amount) amount = item.amount?.replace(/[^a-zA-Z0-9]/g, '_')
      if (item.conjugationAmount) {
        conjugationAmountData = item.conjugationAmount?.replace(
          /[^a-zA-Z0-9]/g,
          '_'
        )
      }
      if (item.priceHeaderError || item.price === null)
        updateError(TubeProperties.PriceHeaderError)

      if (
        item.price &&
        item.price !== TubeProperties.Quote &&
        item.price !== '--'
      ) {
        const formattedPrice = priceFormatter(
          item.price,
          productInformation.currencyFormat,
          productInformation.thousandSeparator,
          productInformation.thousandSpacing,
          productInformation.decimalSeparator,
          productInformation.prefixCurrecny,
          item.currencySymbol
        )
        price = formattedPrice
      } else if (item.price === TubeProperties.Quote) {
        price = intl.formatMessage(messages._PC_QUOTE_ITEM)
      } else {
        price = '--'
      }

      if (
        productDetails.productType === TubeProduct.Aqua ||
        productDetails.productType === TubeProduct.Cpeptide
      ) {
        let nterminalMod = ''
        let cterminalMod = ''
        if (item.nterminalMod) {
          nterminalMod = item.nterminalMod
        }
        if (item.cterminalMod) {
          cterminalMod = item.cterminalMod
        }
        viewSequence = `${nterminalMod}${item.sequence}${cterminalMod}`
      } else {
        if (productDetails.productType === TubeProduct.Wellred) {
          let modValue = ''
          const fiveMod =
            item.fiveMod?.replace(/[[\]]/g, '') !== TubeProperties.None
              ? item.fiveMod
              : ''
          if (fiveMod) {
            modValue = fiveMod
          }
          viewSequence = `${modValue}${item.sequence}`
        } else {
          let fiveModValue = ''
          let threeModValue = ''
          let fiveMod = ''
          let threeMod = ''
          if (item.fiveMod) {
            if (isTwoMeBase(item.fiveMod)) {
              fiveMod = '[' + item.fiveMod + ']'
            } else {
              fiveMod =
                item.fiveMod?.replace(/[[\]]/g, '') !== TubeProperties.None
                  ? item.fiveMod
                  : ''
            }
          }
          if (item.threeMod) {
            if (isTwoMeBase(item.threeMod)) {
              threeMod = '[' + item.threeMod + ']'
            } else {
              threeMod =
                item.threeMod?.replace(/[[\]]/g, '') !== TubeProperties.None
                  ? item.threeMod
                  : ''
            }
          }

          if (fiveMod) {
            fiveModValue = fiveMod
          }
          if (threeMod) {
            threeModValue = threeMod
          }
          viewSequence = `${fiveModValue}${item.sequence}${threeModValue}`
        }
      }
      result.push(
        <Card className={classes.cardWrapper} key={index}>
          <CardContent className={classes.cardContent}>
            <Grid
              container
              direction="row"
              justify="space-between"
              alignItems="flex-start"
            >
              <Grid item sm={3}>
                <div className={GlobalCSS.OligoName}>
                  <h4>
                    <MuiLink onClick={() => displaySessionData(index)}>
                      <strong data-scrub>{item.name}</strong>
                    </MuiLink>
                  </h4>
                </div>
                <div className={classes.sequenceInfo}>
                  <span>
                    {productDetails.productType === TubeProduct.Aqua ||
                    productDetails.productType === TubeProduct.Cpeptide ? (
                      <span>
                        <FormattedMessage {...messages.CONF_TOTAL_AMINO_ACID} />
                        {item.baseCount}
                      </span>
                    ) : (
                      <span>
                        <FormattedMessage {...messages.CONF_TOTAL_BASES} />
                        {item.baseCount}
                      </span>
                    )}
                  </span>
                </div>
              </Grid>
              <Grid item sm={3} className={GlobalCSS.seqWordWrapwithDot}>
                <div>
                  <Tooltip
                    title={
                      <>
                        <div className={GlobalCSS.seqWordWrap}>
                          <FormattedMessage {...messages.CONF_SEQUENCE_LABEL} />
                        </div>
                        <div className={GlobalCSS.sequenceSpacing} data-scrub>
                          {viewSequence}
                        </div>
                      </>
                    }
                    interactive
                    classes={{
                      tooltip: GlobalCSS.InfoPopup,
                      arrow: classes.arrow,
                    }}
                    placement="bottom"
                  >
                    <MuiLink
                      color="primary"
                      className={GlobalCSS.seqWordWrapwithDot}
                      data-scrub
                    >
                      {viewSequence}
                    </MuiLink>
                  </Tooltip>
                </div>
              </Grid>

              <Grid item sm={3} className={GlobalCSS.shiftRight}>
                <div>
                  <Table>
                    <tbody>
                      {productDetails.productType === TubeProduct.EasyOligo ||
                      productDetails.productType === TubeProduct.PureSimple ||
                      productDetails.productType === TubeProduct.Beacon ||
                      productDetails.productType === TubeProduct.Aqua ||
                      productDetails.productType === TubeProduct.Probe ||
                      productDetails.productType === TubeProduct.Wellred ||
                      productDetails.productType === TubeProduct.LongOligo ||
                      productDetails.productType === TubeProduct.iscaledna ||
                      productDetails.productType === TubeProduct.Cpeptide ? (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage {...messages.CONF_AMOUNT} />
                            </strong>
                          </td>
                          <td>
                            {productDetails.productType !== TubeProduct.Aqua &&
                            productDetails.productType !==
                              TubeProduct.Cpeptide &&
                            productDetails.productType !==
                              TubeProduct.iscaledna ? (
                              item.amount ? (
                                item.amount
                              ) : (
                                '--'
                              )
                            ) : amount ? (
                              <FormattedMessage
                                {...checkValidKey(
                                  messages[`_PC_SPEC_${amount}`],
                                  `_PC_SPEC_${amount}`
                                )}
                              />
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_POPUP_SCALE}
                              />{' '}
                            </strong>
                          </td>
                          <td>
                            {scale ? (
                              <FormattedMessage
                                {...checkValidKey(
                                  messages[`_PC_SPEC_${scale}`],
                                  `_PC_SPEC_${scale}`
                                )}
                              />
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                      )}

                      {productDetails.productType !== TubeProduct.Aqua &&
                      productDetails.productType !== TubeProduct.Cpeptide ? (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_PURIFICATION_REVIEW_AND_CONFIRM}
                              />
                            </strong>
                          </td>
                          <td>
                            {item.purification &&
                            item.purification !== undefined ? (
                              <FormattedMessage
                                {...checkValidKey(
                                  messages[`_PC_SPEC_${item.purification}`],
                                  `_PC_SPEC_${item.purification}`
                                )}
                              />
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_PURITY_LEVEL_REVIEW_AND_CONFIRM}
                              />
                            </strong>
                          </td>
                          <td>
                            {item.purityLevel &&
                            item.purityLevel !== undefined ? (
                              <>
                                {productDetails.productType !==
                                TubeProduct.Cpeptide ? (
                                  <FormattedMessage
                                    {...checkValidKey(
                                      messages[`_PC_SPEC_${item.purityLevel}`],
                                      `_PC_SPEC_${item.purityLevel}`
                                    )}
                                  />
                                ) : (
                                  <FormattedMessage
                                    {...checkValidKey(
                                      messages[`PC_SPEC_${item.purityLevel}`],
                                      `PC_SPEC_${item.purityLevel}`
                                    )}
                                  />
                                )}
                              </>
                            ) : (
                              '--'
                            )}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className={classes.sequenceProperties}>
                          <strong>
                            <FormattedMessage
                              {...messages.CONF_FORMAT_REVIEW_AND_CONFIRM}
                            />
                          </strong>
                        </td>
                        <td>
                          {/* STRAT-17701, For RNA JP we are having different key
                          _PC_SPEC_IS_WATER_RNA for format it's value
                          is LIQUID and name is In Solution (TE) and we have to
                          display TE in UI that's why we have to add product and country check
                          because for all other product we have to display In
                          Solution(Water) for LIQUID value , STRAT-20211 for easyoligo also has to display TE
                          in UI even the value comes as LIQUID for Japan*/}

                          {(productDetails.productType === TubeProduct.Rna ||
                            productDetails.productType ===
                              TubeProduct.EasyOligo) &&
                          CountryType.cpeptideJapan ===
                            productDetails.countryCode &&
                          item.format &&
                          item.format !== undefined &&
                          item.format === TubeProperties.LIQUID ? (
                            <FormattedMessage {...messages.TE} />
                          ) : item.format && item.format !== undefined ? (
                            <FormattedMessage
                              {...checkValidKey(
                                messages[`${item.format}`],
                                `${item.format}`
                              )}
                            />
                          ) : (
                            '--'
                          )}
                        </td>
                      </tr>
                      {(productDetails.productType !== TubeProduct.Standard &&
                        productDetails.productType !== TubeProduct.EasyOligo) ||
                      concentration === '' ||
                      concentration === undefined ? (
                        ''
                      ) : (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_CONCENTRATION_EASY_OLIGO_REVIEW_AND_CONFIRM}
                              />
                            </strong>
                          </td>
                          <td>
                            <FormattedMessage
                              {...checkValidKey(
                                messages[`_PC_SPEC_${concentration}`],
                                `_PC_SPEC_${concentration}`
                              )}
                            />
                          </td>
                        </tr>
                      )}
                      {isConjugationDisplayed() ? (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_CONJUGATION}
                              />
                            </strong>
                          </td>
                          <td>
                            <FormattedMessage
                              {...checkValidKey(
                                messages[
                                  `PC_CONJUGATION_${item.conjugation?.toUpperCase()}`
                                ],
                                `PC_CONJUGATION_${item.conjugation?.toUpperCase()}`
                              )}
                            />
                          </td>
                        </tr>
                      ) : (
                        ''
                      )}
                      {isConjugationAmountDisplayed() &&
                      item.conjugation !== TubeProperties.None ? (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_CONJUGATION_AMOUNT}
                              />
                            </strong>
                          </td>
                          <td>
                            <FormattedMessage
                              {...checkValidKey(
                                messages[`_PC_SPEC_${conjugationAmountData}`],
                                `_PC_SPEC_${conjugationAmountData}`
                              )}
                            />
                          </td>
                        </tr>
                      ) : (
                        ''
                      )}
                      {isConjugationChemistryDisplayed() &&
                      item.conjugation !== TubeProperties.None ? (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_CONJUGATION_CHEMISTRY}
                              />
                            </strong>
                          </td>
                          <td>
                            <FormattedMessage
                              {...checkValidKey(
                                messages[
                                  `PC_CONJUGATION_CHEMISTRY_${item.conjugationChemistry}`
                                ],
                                `PC_CONJUGATION_CHEMISTRY_${item.conjugationChemistry}`
                              )}
                            />
                          </td>
                        </tr>
                      ) : (
                        ''
                      )}
                      {isAdditionalCarrierProteinDisplayed() &&
                      item.additionalCarrierProtein ? (
                        <tr>
                          <td className={classes.additionalCarrierCSS}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_ADDITIONAL_CARRIER_PROTEIN}
                              />
                            </strong>
                          </td>
                          <td>
                            <FormattedMessage
                              {...checkValidKey(
                                messages[
                                  `PC_SPEC_ADDITIONAL_CARRIER_PROTEIN_${item.additionalCarrierProtein.toUpperCase()}`
                                ],
                                `PC_SPEC_ADDITIONAL_CARRIER_PROTEIN_${item.additionalCarrierProtein.toUpperCase()}`
                              )}
                            />
                          </td>
                        </tr>
                      ) : (
                        ''
                      )}
                      {isQuantitationDisplayed() ? (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage
                                {...messages.CONF_QUANTITATION}
                              />
                            </strong>
                          </td>
                          <td>{item.quantitation}</td>
                        </tr>
                      ) : (
                        ''
                      )}
                      {isTubeDisplayed() ? (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage {...messages.CONF_TUBES} />
                            </strong>
                          </td>
                          <td>{item.tubes}</td>
                        </tr>
                      ) : (
                        ''
                      )}
                      {productDetails.productType !== TubeProduct.Standard &&
                      productDetails.productType !== TubeProduct.Rna ? (
                        ''
                      ) : (
                        <tr>
                          <td className={classes.sequenceProperties}>
                            <strong>
                              <FormattedMessage {...messages.CONF_MIN_YIELD} />
                            </strong>
                          </td>
                          <td>{item.minimumYield} OD</td>
                        </tr>
                      )}
                      {productDetails.productType === TubeProduct.iscaledna &&
                      item.additionalServices &&
                      item.additionalServices?.length !== 0
                        ? item.additionalServices.map((service, index) => {
                            return (
                              <tr key={service}>
                                {index === 0 ? (
                                  <td className={classes.sequenceProperties}>
                                    <strong>
                                      <FormattedMessage
                                        {...messages.CONF_SERVICES_REVIEW_AND_CONFIRM}
                                      />
                                    </strong>
                                  </td>
                                ) : (
                                  <td
                                    className={classes.sequenceProperties}
                                  ></td>
                                )}
                                <td>
                                  {index ===
                                  item.additionalServices.length - 1 ? (
                                    <FormattedMessage
                                      {...checkValidKey(
                                        messages[`_PC_SPEC_${service}`],
                                        `_PC_SPEC_${service}`
                                      )}
                                    />
                                  ) : (
                                    <>
                                      <FormattedMessage
                                        {...checkValidKey(
                                          messages[`_PC_SPEC_${service}`],
                                          `_PC_SPEC_${service}`
                                        )}
                                      />
                                      {','}
                                    </>
                                  )}
                                </td>
                              </tr>
                            )
                          })
                        : ''}
                    </tbody>
                  </Table>
                </div>
              </Grid>
              <Grid item sm={2} className={GlobalCSS.priceText}>
                <div className={classes.sequenceInfo}>{price}</div>
              </Grid>
              <Grid item sm={1} className={classes.alignRight}>
                <div>
                  <IconButton
                    aria-label="delete"
                    className={classes.deleteButton}
                    onClick={() => {
                      handleDelete(item)
                    }}
                  >
                    <DeleteIcon className={classes.deleteIcon} />
                  </IconButton>
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )
    })
    return result
  }

  return (
    <>
      <div className={classes.header}>
        <h3 className={classes.subHead}>
          <strong>
            <FormattedMessage {...messages.CONF_CONFIRM_ORDER} />
          </strong>
        </h3>

        <div className={scrollBar ? classes.dFlexForZHLang : classes.dFlex}>
          <h4>
            <strong className={clsx(classes.deleteAllText)}>
              <FormattedMessage {...messages.CONF_DELETE_ALL_BUTTON} />
            </strong>
          </h4>
          <span className={classes.deleteMargin}>
            <IconButton
              aria-label="delete"
              onClick={() => {
                setDelDialogOpen(true)
                setDelType('All')
                setDelItem(delItem)
              }}
              className={classes.deleteButton}
              id="tubes-delete-all-button"
              data-testid="tubes-delete-all-button"
            >
              <DeleteIcon className={classes.deleteIcon} />
            </IconButton>
          </span>
        </div>
      </div>
      {isNameWarning ? (
        <Grid>
          <OligoNameWarning
            warning={intl.formatMessage(messages.CONF_OLIGO_NAME_WARNING)}
          />
        </Grid>
      ) : (
        ''
      )}
      <div className={GlobalCSS.oligoCount}>
        {totalOligos()} {sessionData.length}
      </div>
      <TableContainer className={scrollBar ? classes.tableContainer : ''}>
        {renderData()}
      </TableContainer>
      <AddOligo
        productDetails={productDetails}
        updateError={updateError}
        errorMessage={errorMessage}
      />
      <DeleteAllDialog
        dialogOpen={delDialogOpen}
        setDialogOpen={setDelDialogOpen}
        selectedItem={delItem}
        type={delType}
        onDelete={deleteOligoItem}
      />
    </>
  )
}

export default OrderTable
